import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, ArrowLeft, SmilePlus } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { useIsMobile } from "../../hooks/use-mobile";
import { Progress } from "../ui/progress";
import { useTranslation } from "react-i18next";

interface SessionData {
  status: string;
  bot_id: string;
  room_url: string;
  room_name: string;
  token: string;
  message: string;
  caller_id: string;
  intake_type: string;
}

const getFunMessages = (t) => [
  t("intakeProcess.session.funMessages.wellness", "Hana: Wellness Wizard Online!"),
  t("intakeProcess.session.funMessages.journey", "Hana: Wellness Journey Launching!"),
  t("intakeProcess.session.funMessages.energy", "Energy Elevator Going Up!"),
  t("intakeProcess.session.funMessages.health", "Health Hacker Engaged.")
];

const getRandomMessage = (t) => {
  const messages = getFunMessages(t);
  const randomIndex = Math.floor(Math.random() * messages.length);
  return messages[randomIndex];
};

const StartSession: React.FC = () => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [showTalkingMessage, setShowTalkingMessage] = useState(false);
  const [funMessage, setFunMessage] = useState("");
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  
  const sessionData = location.state?.sessionData as SessionData;
  const userPhoneNumber = sessionData?.message?.match(/\+\d+/)?.[0] || t("intakeProcess.session.yourPhone", "your phone");
  const callerPhoneNumber = sessionData?.caller_id || "+16465767547";
  
  let sessionTitle = t("intakeProcess.session.defaultTitle", "Your Session");
  let sessionNumber = 0;
  
  if (sessionData?.intake_type === "intake1") {
    sessionTitle = t("intakeProcess.session.initialAssessment.title");
    sessionNumber = 1;
  } else if (sessionData?.intake_type === "intake2") {
    sessionTitle = t("intakeProcess.session.wellnessPlanning.title");
    sessionNumber = 2;
  } else if (sessionData?.intake_type === "intake3") {
    sessionTitle = t("intakeProcess.session.actionPlanDevelopment.title");
    sessionNumber = 3;
  }

  useEffect(() => {
    // Progress bar animation
    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(interval);
          setShowTalkingMessage(true);
          setFunMessage(getRandomMessage(t));
          return 100;
        }
        return prevProgress + 5; // Increment by 5% every interval
      });
    }, 100); // 20 intervals * 100ms = ~2 seconds

    return () => clearInterval(interval);
  }, [t]);

  const handleGoBack = () => {
    // Use React Router navigation instead of window.history
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(-1); // Go back to previous route in history
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-xl border-gray-200 shadow-md bg-white rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-primary/90 to-primary p-6 text-white">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            {sessionTitle}
          </h1>
          <p className="opacity-90">{t("intakeProcess.session.withHana", "Session {{number}} with Hana", { number: sessionNumber })}</p>
        </div>
        
        <CardContent className={`${isMobile ? 'p-5' : 'p-8'} space-y-8`}>
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              {showTalkingMessage ? 
                <SmilePlus className="h-8 w-8 text-primary animate-bounce" /> :
                <Phone className="h-8 w-8 text-primary" />
              }
            </div>
            
            <h2 className="text-xl md:text-2xl font-semibold mb-2">
              {showTalkingMessage ? 
                funMessage : 
                t("intakeProcess.session.callInitiated", "Your call is being initiated")}
            </h2>
            <p className="text-gray-600 mb-6">
              {showTalkingMessage ? 
                t("intakeProcess.session.inProgress", "Your session is now in progress") : 
                t("intakeProcess.session.callShortly", "You'll receive a call shortly at:")}
            </p>
            
            {!showTalkingMessage && userPhoneNumber !== t("intakeProcess.session.yourPhone", "your phone") && (
              <p className="font-medium text-primary text-lg">{userPhoneNumber}</p>
            )}
            
            <p className="text-sm text-gray-500 mt-2">
              {t("intakeProcess.session.from", "from")} <span className="font-medium text-primary border-2 border-primary/30 bg-primary/5 py-1 px-2 rounded-lg">{callerPhoneNumber}</span>
            </p>
          </div>
          
          {!showTalkingMessage ? (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600">{t("intakeProcess.session.connecting", "Connecting your call")}</span>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: "0ms" }}></div>
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: "300ms" }}></div>
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: "600ms" }}></div>
                </div>
              </div>
              
              <Progress value={progress} className="h-2" />
            </div>
          ) : (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
              <p className="text-green-700">{t("intakeProcess.session.callSoon", "Hana will call you shortly:")}</p>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
            <Button 
              onClick={handleGoBack}
              variant="outline" 
              className="w-full sm:w-auto flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("navigation.back", "Go Back")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StartSession;
